import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { PublicHeader } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { apiClient, auth } from "@/lib/api";
import { TIDS } from "@/lib/testIds";
import { Shield, CheckCircle2, XCircle, KeyRound } from "lucide-react";

export default function EmployeeSignup() {
  const navigate = useNavigate();
  const location = useLocation();
  const presetCode = (new URLSearchParams(location.search).get("code") || "").trim().toUpperCase();
  const [form, setForm] = useState({
    invite_code: presetCode,
    full_name: "",
    email: "",
    password: "",
  });
  const [codeState, setCodeState] = useState({ checking: false, valid: null, label: "", error: "" });
  const [loading, setLoading] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    const code = (form.invite_code || "").trim().toUpperCase();
    if (!code) {
      setCodeState({ checking: false, valid: null, label: "", error: "" });
      return;
    }
    setCodeState((s) => ({ ...s, checking: true }));
    const t = setTimeout(() => {
      apiClient
        .get(`/employees/invite/${encodeURIComponent(code)}`)
        .then((r) => setCodeState({ checking: false, valid: true, label: r.data.label || "", error: "" }))
        .catch((err) =>
          setCodeState({
            checking: false,
            valid: false,
            label: "",
            error: err?.response?.data?.detail || "Invalid invite code",
          })
        );
    }, 300);
    return () => clearTimeout(t);
  }, [form.invite_code]);

  const submit = async (e) => {
    e.preventDefault();
    if (codeState.valid !== true) {
      toast.error("Enter a valid invite code first");
      return;
    }
    if (form.password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    setLoading(true);
    try {
      const res = await apiClient.post("/employees/signup", form);
      auth.setSession(res.data.access_token, "employee", res.data.user);
      toast.success("Welcome to the team!");
      navigate("/employee/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Signup failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <PublicHeader />
      <div className="mx-auto max-w-md px-6 py-16 md:py-24">
        <div className="text-center mb-8">
          <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-50 text-blue-600 mx-auto">
            <Shield className="h-6 w-6" />
          </span>
          <h1 className="mt-5 text-3xl font-heading font-medium tracking-tight text-slate-900">Join the team</h1>
          <p className="mt-2 text-slate-600 text-sm">Internal employee signup — invite-only.</p>
        </div>

        <Card className="rounded-2xl border-slate-200 shadow-sm p-7">
          <form onSubmit={submit} data-testid={TIDS.employeeSignupForm} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="invite_code">Invite code</Label>
              <div className="relative">
                <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  id="invite_code"
                  data-testid={TIDS.employeeSignupCode}
                  value={form.invite_code}
                  onChange={(e) => update("invite_code", e.target.value.toUpperCase())}
                  className="pl-9 font-mono tracking-wider"
                  required
                />
              </div>
              {codeState.checking ? <p className="text-xs text-slate-500">Checking…</p> : null}
              {codeState.valid === true ? (
                <p className="text-xs text-emerald-700 flex items-center gap-1">
                  <CheckCircle2 className="h-3.5 w-3.5" /> Valid invite{codeState.label ? ` — "${codeState.label}"` : ""}
                </p>
              ) : null}
              {codeState.valid === false ? (
                <p className="text-xs text-rose-700 flex items-center gap-1">
                  <XCircle className="h-3.5 w-3.5" /> {codeState.error}
                </p>
              ) : null}
            </div>
            <div className="space-y-2">
              <Label htmlFor="full_name">Full name</Label>
              <Input
                id="full_name"
                data-testid={TIDS.employeeSignupName}
                value={form.full_name}
                onChange={(e) => update("full_name", e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Work email</Label>
              <Input
                id="email"
                type="email"
                data-testid={TIDS.employeeSignupEmail}
                value={form.email}
                onChange={(e) => update("email", e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password <span className="text-xs text-slate-500">(min 8 characters)</span></Label>
              <Input
                id="password"
                type="password"
                data-testid={TIDS.employeeSignupPassword}
                value={form.password}
                onChange={(e) => update("password", e.target.value)}
                minLength={8}
                required
              />
            </div>
            <Button
              type="submit"
              disabled={loading || codeState.valid === false}
              data-testid={TIDS.employeeSignupSubmit}
              className="w-full rounded-full bg-blue-600 hover:bg-blue-700 h-11"
            >
              {loading ? "Creating account…" : codeState.checking ? "Checking code…" : "Create account"}
            </Button>
          </form>
        </Card>

        <p className="text-center text-sm text-slate-500 mt-6">
          Already on the team? <Link to="/employee/login" className="text-blue-600 hover:underline font-medium">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
